import React, { useState } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.svg';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  page: { minHeight: '100vh', backgroundColor: '#f9fafb', fontFamily: 'inherit' },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '0 5%', height: '64px', backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 100,
  },
  
  backBtn: {
    padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb',
    backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  container: { maxWidth: '1000px', margin: '0 auto', padding: '60px 20px' },
  title: { fontSize: '36px', fontWeight: '800', color: '#111827', textAlign: 'center', marginBottom: '8px' },
  sub: { fontSize: '16px', color: '#6b7280', textAlign: 'center', marginBottom: '40px' },
  toggle: {
    display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '48px',
    backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '4px',
    width: 'fit-content', margin: '0 auto 48px',
  },
  toggleBtn: {
    padding: '8px 28px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600', backgroundColor: 'transparent',
    color: '#6b7280', fontFamily: 'inherit',
  },
  toggleBtnActive: { backgroundColor: '#fff', color: '#111827', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  saving: { fontSize: '11px', color: '#16a34a', fontWeight: '700', marginLeft: '6px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '60px' },
  card: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
    border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    display: 'flex', flexDirection: 'column',
  },
  cardPro: {
    backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
    border: '2px solid #0052CC', boxShadow: '0 4px 20px rgba(0,82,204,0.15)',
    position: 'relative', display: 'flex', flexDirection: 'column',
  },
  popularBadge: {
    position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#0052CC', color: '#fff', fontSize: '11px', fontWeight: '700',
    padding: '4px 14px', borderRadius: '20px', whiteSpace: 'nowrap',
  },
  planName: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  planDesc: { fontSize: '13px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.5' },
  price: { fontSize: '42px', fontWeight: '800', color: '#0052CC', lineHeight: 1 },
  priceFree: { fontSize: '42px', fontWeight: '800', color: '#111827', lineHeight: 1 },
  priceCustom: { fontSize: '32px', fontWeight: '800', color: '#111827', lineHeight: 1 },
  priceSub: { fontSize: '13px', color: '#6b7280', marginBottom: '24px', marginTop: '4px' },
  divider: { height: '1px', backgroundColor: '#f3f4f6', margin: '20px 0' },
  features: { listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 },
  feature: { fontSize: '13px', color: '#374151', display: 'flex', gap: '8px', alignItems: 'flex-start', lineHeight: '1.4' },
  featureCheck: { color: '#0052CC', fontWeight: '700', flexShrink: 0, marginTop: '1px' },
  featureGray: { fontSize: '13px', color: '#9ca3af', display: 'flex', gap: '8px', alignItems: 'flex-start', lineHeight: '1.4' },
  featureDash: { color: '#d1d5db', flexShrink: 0 },
  btn: {
    width: '100%', padding: '13px', borderRadius: '10px', border: 'none',
    cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit',
    marginTop: 'auto',
  },
  btnPrimary: { backgroundColor: '#0052CC', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnEnterprise: { backgroundColor: '#111827', color: '#fff' },
  faqSection: { maxWidth: '680px', margin: '0 auto', paddingBottom: '60px' },
  faqTitle: { fontSize: '24px', fontWeight: '700', color: '#111827', textAlign: 'center', marginBottom: '32px' },
  faqItem: { marginBottom: '8px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' },
  faqQ: { fontSize: '14px', fontWeight: '700', color: '#111827', flex: 1 },
  faqA: { fontSize: '13px', color: '#6b7280', lineHeight: '1.6', padding: '0 24px 20px' },
  faqHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', cursor: 'pointer' },
};

const PLANS = [
  {
    id: 'standard',
    name: 'Standard',
    desc: 'For students, clinicians, and anyone exploring V2VLab.',
    price: { monthly: '$0', annual: '$0' },
    priceSub: { monthly: 'Free forever', annual: 'Free forever' },
    cardStyle: styles.card,
    btnStyle: styles.btnSecondary,
    btnLabel: (plan) => plan === 'standard' ? 'Current plan' : 'Get started free',
    btnAction: (navigate, currentUser, plan) => {
      if (!currentUser) navigate('/signup');
    },
    features: [
      { text: '50 turns/day, 750/month (Lab + Practice)', included: true },
      { text: '2 agents per scenario', included: true },
      { text: 'Max 6 turns per simulation', included: true },
      { text: 'Voice + STT mode', included: true },
      { text: 'Acoustic perturbation (categorical + parametric)', included: true },
      { text: 'Basic evaluation (overall score)', included: true },
      { text: 'Claude (Anthropic) only', included: true },
      { text: 'Full evaluation engine', included: false },
      { text: 'Multi-agent (3–6 agents)', included: false },
      { text: 'Corpus export', included: false },
      { text: 'REST API access', included: false },
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    desc: 'For speech scientists, researchers, and clinical educators.',
    price: { monthly: '$50', annual: '$42' },
    priceSub: { monthly: 'per month + TTS overage', annual: 'per month, billed $500/year + overage' },
    cardStyle: styles.cardPro,
    popular: true,
    btnStyle: styles.btnPrimary,
    btnLabel: (plan) => plan === 'professional' ? 'Current plan' : plan === 'enterprise' ? 'Included' : 'Upgrade to Professional',
    features: [
      { text: 'No simulation cap', included: true },
      { text: 'Voice simulations — 100,000 TTS characters/month included', included: true },
      { text: 'Overage rate apply if monthly TTS limit is exceeded', included: true },
      { text: 'Up to 6 agents per scenario', included: true },
      { text: 'Acoustic perturbation (categorical + parametric)', included: true },
      { text: 'Full 3-layer evaluation engine', included: true },
      { text: 'Multiple LLM providers', included: true },
      { text: 'Full simulation history', included: true },
      { text: 'WER analytics + disorder analysis', included: true },
      { text: 'Corpus export (JSON + CSV)', included: true },
      { text: 'REST API access', included: true },
      { text: 'Email support (48hr)', included: true },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    desc: 'For universities, hospital systems, and research centers.',
    price: { monthly: 'Custom', annual: 'Custom' },
    priceSub: { monthly: 'Annual contract', annual: 'Annual contract' },
    cardStyle: styles.card,
    btnStyle: styles.btnEnterprise,
    btnLabel: () => 'Contact Sales',
    btnAction: () => { window.location.href = 'mailto:info@converstation.ai?subject=V2VLab Enterprise Inquiry'; },
    features: [
      { text: 'Everything in Professional', included: true },
      { text: 'Team accounts', included: true },
      { text: 'SSO / institutional auth', included: true },
      { text: 'Batch simulation runs via API', included: true },
      { text: 'Custom disorder profiles', included: true },
      { text: 'Custom data retention policy', included: true },
      { text: 'SLA guarantee', included: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Annual invoicing', included: true },
      { text: 'White-label option', included: true },
    ],
  },
];

const FAQS = [
  {
    q: 'Can I try V2VLab before paying?',
    a: 'Yes — the Standard plan is free forever. You get 50 lab and practice turns per day, full Voice+STT mode, and both categorical and parametric acoustic perturbation controls. No credit card required.',
  },
  {
    q: 'How does TTS overage billing work?',
    a: 'Professional includes 100,000 TTS characters per month. Additional usage is billed at $0.70 per 1,000 characters — automatically calculated and added to your monthly Stripe invoice. Monitor usage on your Dashboard.',
  },
  {
    q: 'What happens when I hit the daily or monthly turn limit?',
    a: 'Standard is capped at 50 turns per day and 750 per month, combined across Lab and Practice. A turn is generated each time an agent speaks. If you hit the limit mid-conversation, you\'ll see a message with an option to upgrade. Daily limits reset at midnight UTC, monthly on the 1st. Professional users have no turn caps — voice usage is metered via TTS characters, and LLM usage is subject to the selected provider\'s own limits.',
  },
  {
    q: 'Can I cancel my Professional subscription anytime?',
    a: 'Yes — cancel anytime from the billing portal in your profile. Your plan stays active until the end of the billing period. No cancellation fees.',
  },
  {
    q: 'What LLM providers are available?',
    a: 'Standard users have access to Claude (Anthropic). Professional and Enterprise users have access to all three: Claude (Anthropic), GPT-4o (OpenAI), and Gemini (Google).',
  },
  {
    q: 'Is there a discount for annual billing?',
    a: 'Yes — annual billing for Professional is $500/year, saving you ~17% compared to monthly ($600/year). Overage charges still apply monthly.',
  },
  {
    q: 'What is the Enterprise plan?',
    a: 'Enterprise is a custom plan for universities, hospital systems, and research centers needing team accounts, SSO, batch API runs, SLA guarantees, and dedicated support. Contact us at info@converstation.ai for a quote.',
  },
];

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.faqItem}>
      <div style={styles.faqHeader} onClick={() => setOpen(o => !o)}>
        <div style={styles.faqQ}>{faq.q}</div>
        <span style={{ fontSize: '16px', color: '#6b7280', marginLeft: '12px', flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && <div style={styles.faqA}>{faq.a}</div>}
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { currentUser, userProfile, isPro } = useAuth();
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const currentPlan = userProfile?.plan || 'standard';
  const width = useWindowWidth();
  const isMobile = width < 768;
  const isTablet = width < 1024;

  function handleBack() {
    if (currentUser) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  }

  async function handleUpgrade() {
    if (!currentUser) { navigate('/register'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_id: currentUser.uid,
          email: currentUser.email,
          billing,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      {/* Nav */}
      <nav style={{ ...styles.nav, padding: isMobile ? '0 16px' : '0 5%' }}>
        <img src={logo} alt="V2VLab" style={{ height: '32px', width: 'auto', cursor: 'pointer' }} onClick={() => navigate('/')} />
        <button style={styles.backBtn} onClick={handleBack}>
          ← {currentUser ? 'Back to Dashboard' : 'Back to Home'}
        </button>
      </nav>

      <div style={{ ...styles.container, padding: isMobile ? '32px 16px' : '60px 20px' }}>
        <div style={styles.title}>Simple, transparent pricing</div>
        <div style={styles.sub}>Start free. Upgrade when your research demands more.</div>

        {/* Billing toggle */}
        <div style={{ ...styles.toggle, width: isMobile ? '100%' : 'fit-content' }}>
          {[
            { id: 'monthly', label: 'Monthly' },
            { id: 'annual', label: <span>Annual <span style={styles.saving}>Save 17%</span></span> },
          ].map(b => (
            <button
              key={b.id}
              style={{ ...styles.toggleBtn, ...(billing === b.id ? styles.toggleBtnActive : {}) }}
              onClick={() => setBilling(b.id)}
            >
              {b.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ color: '#dc2626', textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {/* Plan cards */}
        <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3, 1fr)' }}>
          {PLANS.map(plan => (
            <div key={plan.id} style={plan.cardStyle}>
              {plan.popular && <div style={styles.popularBadge}>Most Popular</div>}
              <div style={styles.planName}>{plan.name}</div>
              <div style={styles.planDesc}>{plan.desc}</div>
              <div style={plan.id === 'standard' ? styles.priceFree : plan.id === 'enterprise' ? styles.priceCustom : styles.price}>
                {plan.price[billing]}
              </div>
              <div style={styles.priceSub}>{plan.priceSub[billing]}</div>
              <div style={styles.divider} />
              <ul style={styles.features}>
                {plan.features.map((f, i) => (
                  <li key={i} style={f.included ? styles.feature : styles.featureGray}>
                    <span style={f.included ? styles.featureCheck : styles.featureDash}>
                      {f.included ? '✓' : '–'}
                    </span>
                    {f.text}
                  </li>
                ))}
              </ul>
              <button
                style={{ ...styles.btn, ...plan.btnStyle }}
                disabled={
                  loading ||
                  (plan.id === 'professional' && (currentPlan === 'professional' || currentPlan === 'enterprise')) ||
                  (plan.id === 'standard' && currentUser && currentPlan === 'standard')
                }
                onClick={() => {
                  if (plan.btnAction) {
                    plan.btnAction(navigate, currentUser, currentPlan);
                  } else if (plan.id === 'professional') {
                    handleUpgrade();
                  }
                }}
              >
                {loading && plan.id === 'professional' ? 'Redirecting...' : plan.btnLabel(currentPlan)}
              </button>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginBottom: '40px', marginTop: '-40px' }}>
          * LLM usage subject to provider limits (Anthropic, OpenAI, Google). Voice simulation usage metered via TTS characters.
        </div>

        {/* FAQ */}
        <div style={styles.faqSection}>
          <div style={styles.faqTitle}>Frequently asked questions</div>
          {FAQS.map((faq, i) => (
            <FaqItem key={i} faq={faq} />
          ))}
        </div>
      </div>
    </div>
  );
}
